/**
 * Проверка ответов служб на границе (D-056): тип TypeScript ответ не проверяет,
 * проверяет декодер, и неожиданный ответ становится `invalid-response`, а не
 * пустым экраном. Общие кирпичи декодеров пакета AQSI.
 */

export type WireObject = Record<string, unknown>

export function fail(message: string): never {
  throw new Error(message)
}

export function objectOf(value: unknown, field: string): WireObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as WireObject)
    : fail(`${field} — не объект`)
}

export function arrayOf(value: unknown, field: string): readonly unknown[] {
  return Array.isArray(value) ? value : fail(`${field} — не массив`)
}

export function textOf(value: unknown, field: string): string {
  return typeof value === 'string' ? value : fail(`${field} — не строка`)
}

export function idOf(value: unknown, field: string): string {
  const text = textOf(value, field)
  return text.trim() !== '' ? text : fail(`${field} — пустой идентификатор`)
}

export function optionalText(value: unknown, field: string): string | null {
  return value === null || value === undefined ? null : textOf(value, field)
}

export function numberOf(value: unknown, field: string): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fail(`${field} — не число`)
}

export function optionalNumber(value: unknown, field: string): number | null {
  return value === null || value === undefined ? null : numberOf(value, field)
}

export function boolOf(value: unknown, field: string): boolean {
  return typeof value === 'boolean' ? value : fail(`${field} — не логическое`)
}

export function optionalBool(value: unknown, field: string): boolean | null {
  return value === null || value === undefined ? null : boolOf(value, field)
}

export function textList(value: unknown, field: string): readonly string[] {
  return arrayOf(value, field).map((item, index) => textOf(item, `${field}[${index}]`))
}

/** `ResourceId` без JSON-конвертера сериализуется объектом `{ value }` (C2), в конверте C3a — строкой. */
export function ridOf(value: unknown, field: string): string {
  if (typeof value === 'string') return idOf(value, field)
  return idOf(objectOf(value, field).value, `${field}.value`)
}

/** GUID сравнивается без учёта регистра: сервер пишет строчными, адрес окна — как ввёл человек. */
export function sameId(left: string, right: string): boolean {
  return left.toLowerCase() === right.toLowerCase()
}

/**
 * Ответ чужого ресурса не принимается (R07): rid ответа обязан совпасть с rid
 * запроса. Иначе ошибка маршрутизации Gateway показала бы кассы AQSI-B в окне AQSI-A.
 */
export function requireRid(actual: string, expected: string, field: string): void {
  if (!sameId(actual, expected)) fail(`${field}: ответ ресурса ${actual}, а запрошен ${expected}`)
}
